import { getFullName } from '@/lib';
import { usePermissions } from '@/hooks/usePermissions';
import { useDashboardSnapshots } from '@/hooks/useDashboardSnapshots';
import { useAppSelector } from '@/store/hooks';
import {
  selectActiveOrganization,
  selectActiveOrganizationId,
  selectActiveMembershipForCurrentUser,
  selectActiveRoleForCurrentUser,
  selectCurrentUserProfile,
} from '@/store/selectors/authorizationSelectors';
import { normalizeRoleKey } from '@/domain/entities/role';
import { Permissions } from '@/lib/permissions';
import { OrgDashboard } from './dashboard/OrgDashboard';
import { SuperAdminDashboard } from './dashboard/SuperAdminDashboard';

function formatRoleLabel(roleKey?: string, roleName?: string) {
  if (roleName?.trim()) return roleName.trim();
  const value = roleKey?.trim() ?? '';
  if (!value) return 'No role';
  const normalized = normalizeRoleKey(value);
  return normalized.replace(/_/g, ' ');
}

const HomePage = () => {
  const { hasAnyPermission, isSuperAdmin } = usePermissions();
  const { orgSnapshot, superAdminSnapshot } = useDashboardSnapshots();
  const user = useAppSelector(selectCurrentUserProfile);
  const activeOrganization = useAppSelector(selectActiveOrganization);
  const activeOrganizationId = useAppSelector(selectActiveOrganizationId);
  const activeMembership = useAppSelector(selectActiveMembershipForCurrentUser);
  const activeRole = useAppSelector(selectActiveRoleForCurrentUser);
  const fullName = getFullName(user);
  const detailsOrgId = activeOrganizationId ?? activeMembership?.id;
  const canViewOrganization = isSuperAdmin || hasAnyPermission([Permissions.MyOrgRead, Permissions.MyOrgUpdate]);
  const currentOrgContext =
    activeOrganization || activeMembership
      ? {
          organizationName: activeOrganization?.name,
          roleLabel: formatRoleLabel(activeMembership?.roleKey, activeRole?.name),
          organizationDetailsHref:
            canViewOrganization && detailsOrgId
              ? `/admin/organizations/${encodeURIComponent(detailsOrgId)}`
              : undefined,
        }
      : null;

  if (isSuperAdmin) {
    return (
      <SuperAdminDashboard fullName={fullName} snapshot={superAdminSnapshot} currentOrgContext={currentOrgContext} />
    );
  }

  return <OrgDashboard fullName={fullName} snapshot={orgSnapshot} currentOrgContext={currentOrgContext} />;
};

export default HomePage;
